import React from 'react';
import { Badge } from '@cargomatic/ui';
import { humanize } from 'underscore.string';
import { format } from 'date-fns';
import { CircleCheckIcon, CircleSlashIcon } from 'lucide-react';
import moment from 'moment';
import StatusBadge from './StatusBadge';
import { CustomerTypeBadge } from './CustomerTypeBadge';
import _ from 'lodash'

export const formatStatusCell = ({ row }) => {
  const value = row.getValue('status');

  return <StatusBadge status={value} />;
};

export function formatBooleanValue(value) {
  if (value) {
    return (
      <div className="flex justify-end text-right">
        <CircleCheckIcon className="w-4 h-4 text-green-500 text-right" />
      </div>
    );
  }
  if (value === false) {
    return (
      <div className="flex justify-end text-right">
        <CircleSlashIcon className="w-4 h-4 text-red-500 text-right" />
      </div>
    );
  }
  return null;
}

export function formatTextValue(value) {
  return value || '-';
}

export function formatNumericValue(value) {
  return <div className="text-right">{value || '-'}</div>;
}

export const formatSequenceCell = ({ row }) => {
  const sequence = row.original.sequenceCount;

  const sequenceCount = row.original.totalCount;

  if (!sequence || !sequenceCount) {
    return '-';
  }

  return `${sequence} of ${sequenceCount} `;
};

export const formatCustomerTypeCell = ({ row }) => {
  const value = row.getValue('customerType');
  return <CustomerTypeBadge customerType={value} />;
};

export function formatCustomerCell({ row }) {
  return formatTextValue(row.getValue('customerName'));
}

export const formatRateCell = ({ row }) => {
  const rateValue = row.getValue('rate');
  if (!rateValue) {
    return formatNumericValue('-');
  }
  const amount = parseFloat(row.getValue('rate')).toFixed(2);
  const formatted = new Intl.NumberFormat('en-us', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);

  return formatNumericValue(formatted);
};

function formatDateCell(date) {
  const value = date ? format(date, 'MM/dd/yyyy') : '-';

  return formatNumericValue(value);
}

export const formatUpdatedAtCell = ({ row }) => {
  const rowValue = row.getValue('laneRatesUpdatedAt');
  return rowValue ? moment(rowValue).fromNow(true) : '-';
};

export const formatCreatedAtCell = ({ row }) => {
  const rowValue = row.getValue('laneRatesCreatedAt');
  return formatDateCell(rowValue);
};

export const formatValidityStart = ({ row }) => {
  const rowValue = row.getValue('effectiveStart');
  return formatDateCell(rowValue);
};

export const formatValidityEnd = ({ row }) => {
  const rowValue = row.getValue('effectiveEnd');
  return formatDateCell(rowValue);
};

export const formatReversibleCell = ({ row }) => {
  const reversible = row.getValue('reversible');

  return formatBooleanValue(reversible);
};

export const formatCommittmentCell = ({ row }) => {
  const value = row.getValue('commitment');

  return formatNumericValue(value);
};

export function getRateConditions(row) {
  return row.original?.conditions || {};
}

export function formatHazardous(criteria) {
  if (_.isEmpty(criteria))
    return criteria;
  return criteria === 'Y' ? 'Yes' : 'No';
};

export function getRateConditionCriteria(key, row) {
  const criteria = getRateConditions(row)?.[key]?.criteria
  if (['hazmat', 'hazardous'].includes(key))
    return formatHazardous(criteria);
  return criteria; 
}

export const formatCustomerRateConditionCell = ({ row }) =>
  formatTextValue(getRateConditionCriteria('customer', row));

export const formatEquipmenSizeRateConditionCell = ({ row }) => (
  <div className="text-right">
    {formatTextValue(getRateConditionCriteria('equipmentSize', row))}
  </div>
);

export const formatEquipmenTypeRateConditionCell = ({ row }) =>
  formatTextValue(getRateConditionCriteria('equipmentType', row));

export const formatHazmatRateConditionCell = ({ row }) =>
  formatTextValue(getRateConditionCriteria('hazmat', row));

export const formatMarketRateConditionCell = ({ row }) =>
  formatTextValue(getRateConditionCriteria('market', row));

export const formatOrderTypeRateConditionCell = ({ row }) =>
  formatTextValue(getRateConditionCriteria('orderType', row));

export const formatSteamshipLineRateConditionCell = ({ row }) =>
  formatTextValue(getRateConditionCriteria('steamShipLine', row));
